import { useEffect, useMemo, useState } from 'react'
import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useLocale } from '@/contexts/LocaleContext'
import { useTeam } from '@/hooks/useTeam'
import { useEdition } from '@/hooks/useEdition'
import { projectService } from '@/services/projectService'
import type { Project } from '@/services/projectTypes'
import { emotionAnalysisService, type ProjectEmotionSummary } from '@/services/emotionAnalysisService'
import ResearcherShell from '@/components/researcher/ResearcherShell'
import StudiesTable, { type StudyRow } from '@/components/researcher/StudiesTable'
import { createRelativeFormatter, toStudyRow } from '@/components/researcher/studyRows'
import { FA_COLOR, type EmotionKey } from '@/components/researcher/liveDemo/constants'

export default function ProjectsList() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const { language } = useLocale()
  const navigate = useNavigate()
  const { inTeam } = useTeam()
  const { isBasic } = useEdition()

  const [projects, setProjects] = useState<Project[] | null>(null)
  // undefined keys render a loading skeleton in the table's emotion columns.
  const [emotions, setEmotions] = useState<Record<string, ProjectEmotionSummary> | null>(null)

  useEffect(() => {
    let cancelled = false
    projectService
      .getProjects()
      .then((data) => !cancelled && setProjects(data))
      .catch(() => !cancelled && setProjects([]))
    emotionAnalysisService
      .summaries()
      .then((data) => !cancelled && setEmotions(data))
      .catch(() => !cancelled && setEmotions({}))
    return () => {
      cancelled = true
    }
  }, [])

  const rows = useMemo<StudyRow[]>(() => {
    if (!projects) return []
    const dateFmt = new Intl.DateTimeFormat(language, { day: 'numeric', month: 'long' })
    const relative = createRelativeFormatter(language)
    return projects.map((p) => ({
      ...toStudyRow(p, dateFmt.format, relative),
      emotion: emotions ? emotions[String(p.id)] ?? null : undefined,
    }))
  }, [projects, emotions, language])

  const counts = useMemo(
    () => ({
      all: rows.length,
      active: rows.filter((r) => r.status === 'active' || r.status === 'paused').length,
      completed: rows.filter((r) => r.status === 'done' || r.status === 'review').length,
      drafts: rows.filter((r) => r.status === 'draft').length,
    }),
    [rows],
  )

  const goToWizard = () => navigate('/studies/new')
  const loading = projects === null
  const isEmpty = !loading && rows.length === 0

  return (
    <ResearcherShell active="studies" crumbs={[t('projectsPage.title')]} onPrimaryAction={goToWizard}>
      <Stack spacing={3} sx={{ maxWidth: 1340, mx: 'auto', px: { xs: 2, md: 0 } }}>
        <Box>
          <Typography variant="h1" sx={{ fontSize: { xs: 24, md: 32 } }}>
            {t('projectsPage.title')}
          </Typography>
          <Typography sx={{ fontSize: 15, color: c.ink56, mt: 0.5 }}>{t('projectsPage.subtitle')}</Typography>
        </Box>

        {!loading && rows.length > 0 && <OverviewStats rows={rows} active={counts.active} total={counts.all} />}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : isEmpty ? (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: `${theme.affexy.radii.lg / 1.33}px`,
              borderColor: 'divider',
              p: { xs: 4, md: 8 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              textAlign: 'center',
            }}
          >
            <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: c.gray50, display: 'grid', placeItems: 'center' }}>
              <FolderOpenOutlinedIcon sx={{ fontSize: 26, color: c.ink48 }} />
            </Box>
            <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 20 }}>
              {t('projectsPage.emptyTitle')}
            </Typography>
            <Typography sx={{ fontSize: 14, color: c.ink56, maxWidth: 420 }}>{t('projectsPage.emptyBody')}</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={goToWizard} sx={{ mt: 1 }}>
              {t('projectsPage.newProject')}
            </Button>
          </Paper>
        ) : (
          <StudiesTable
            rows={rows}
            counts={counts}
            showScope={inTeam}
            title={t('projectsPage.tableTitle')}
            subtitle={t('projectsPage.tableSubtitle')}
            onRowAction={(row) => {
              // /studies/:id/results is Pro-only (it is the analytics mock-up).
              // FaceWise reads a finished study on the study screen itself,
              // which is where its real results panel lives.
              if (!isBasic && (row.status === 'review' || row.status === 'done'))
                navigate(`/studies/${row.id}/results`)
              else if (row.status === 'draft') navigate(`/studies/new?draft=${row.id}`)
              else navigate(`/studies/${row.id}`)
            }}
          />
        )}
      </Stack>
    </ResearcherShell>
  )
}

/**
 * Compact KPI strip summarising every study at a glance — replaces the old
 * respondent-links card. All figures are derived from data already loaded for
 * the table (study rows + their emotion summaries), so it adds no extra fetch.
 */
function OverviewStats({ rows, active, total }: { rows: StudyRow[]; active: number; total: number }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const { t: tEmo } = useTranslation('liveDemo')

  const stats = useMemo(() => {
    const withData = rows.filter((r) => r.emotion?.has_data)
    const responses = rows.reduce((sum, r) => sum + r.responses.n, 0)
    const target = rows.reduce((sum, r) => sum + r.responses.t, 0)
    const avgReactivity = withData.length
      ? withData.reduce((sum, r) => sum + (r.emotion?.reactivity ?? 0), 0) / withData.length
      : null
    // Most frequent dominant emotion across studies that have any signal.
    const tally = new Map<EmotionKey, number>()
    for (const r of withData) {
      const d = r.emotion?.dominant
      if (d) tally.set(d, (tally.get(d) ?? 0) + 1)
    }
    let topEmotion: EmotionKey | null = null
    let best = 0
    tally.forEach((n, k) => {
      if (n > best) {
        best = n
        topEmotion = k
      }
    })
    return { responses, target, avgReactivity, topEmotion: topEmotion as EmotionKey | null }
  }, [rows])

  const items = [
    {
      label: t('projectsPage.overview.activeStudies'),
      value: String(active),
      sub: t('projectsPage.overview.ofTotal', { total }),
    },
    {
      label: t('projectsPage.overview.responses'),
      value: String(stats.responses),
      sub: t('projectsPage.overview.ofTarget', { target: stats.target }),
    },
    {
      label: t('projectsPage.overview.avgReactivity'),
      value: stats.avgReactivity === null ? '—' : `${Math.round(stats.avgReactivity * 100)}%`,
      sub: t('projectsPage.overview.acrossWithData'),
    },
  ]

  const labelSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: c.ink48,
  }
  const valueSx = {
    fontFamily: theme.affexy.fonts.heading,
    fontWeight: 700,
    fontSize: 26,
    color: c.ink,
    mt: 0.5,
    fontVariantNumeric: 'tabular-nums' as const,
  }
  const subSx = { fontSize: 12.5, color: c.ink56, mt: 0.25 }

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: `${theme.affexy.radii.md}px`,
        borderColor: 'divider',
        display: 'flex',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
      }}
    >
      {items.map((it, i) => (
        <Box
          key={it.label}
          sx={{ flex: { xs: '1 1 50%', md: 1 }, p: 2.5, borderLeft: { md: i ? `1px solid ${c.ink08}` : 'none' } }}
        >
          <Typography sx={labelSx}>{it.label}</Typography>
          <Typography sx={valueSx}>{it.value}</Typography>
          <Typography sx={subSx}>{it.sub}</Typography>
        </Box>
      ))}
      <Box sx={{ flex: { xs: '1 1 50%', md: 1 }, p: 2.5, borderLeft: { md: `1px solid ${c.ink08}` } }}>
        <Typography sx={labelSx}>{t('projectsPage.overview.topEmotion')}</Typography>
        {stats.topEmotion ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '4px', background: FA_COLOR[stats.topEmotion], flexShrink: 0 }} />
            <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 22, color: c.ink }}>
              {tEmo(`emotions.${stats.topEmotion}`)}
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ ...valueSx, color: c.ink48 }}>—</Typography>
        )}
        <Typography sx={subSx}>{t('projectsPage.overview.topEmotionSub')}</Typography>
      </Box>
    </Paper>
  )
}
